
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { DebugDateProvider } from "./contexts/DebugDateContext";
import { GameModeProvider, useGameMode } from "./contexts/GameModeContext";
import { GameConfigProvider } from "./contexts/GameConfigContext";
import Index from "./pages/Index";
import GamePage from "./pages/GamePage";
import BabbleRanks from "./pages/BabbleRanks";
import HowToPlay from "./pages/HowToPlay";
import WelcomePage from "./pages/WelcomePage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminPosts from "./pages/admin/AdminPosts";
import AdminPostEditor from "./pages/admin/AdminPostEditor";
import AdminGate from "./pages/admin/AdminGate";
import AdminStats from "./pages/admin/AdminStats";
import { Layout } from "./components/layout/Layout";
import { useState, useEffect } from "react";
import { animationCoordinator } from "./lib/managers/AnimationCoordinator";
import { timeoutManager } from "./lib/managers/TimeoutManager";
import { directCookieAccess } from "./lib/storage/StorageAdapter";
import { SharedSequenceNotification } from "./components/notifications/SharedSequenceNotification";
import { sharedSequenceStateManager } from "./components/game/managers/SharedSequenceStateManager";
import { checkAndClearGameData } from "./lib/clearGameData";
import ScrollToTop from "./components/ScrollToTop";
import { ViewModeProvider } from "./contexts/ViewModeContext";

// Create a more resilient query client with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: false,
    },
  },
});

// CRITICAL FIX: Add a router guard component to check for redirects early
const GameRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { switchToMode } = useGameMode();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Only run this check on /game route
    if (location.pathname === '/game' && !checked) {
      
      // Add a small delay to allow localStorage to be read and game mode to be established
      const checkRedirect = async () => {
        setChecked(true);
        
        // Skip redirect if coming from welcome page (check both storage types and mode-specific flags)
        const fromWelcomeLS = localStorage.getItem('from_welcome_page') === 'true';
        const fromWelcomeSS = sessionStorage.getItem('from_welcome_page') === 'true';
        
        // Determine current mode - check URL params first, then localStorage, then default to daily
        const urlParams = new URLSearchParams(window.location.search);
        const urlMode = urlParams.get('mode') as 'daily' | 'practice' | null;
        const storedMode = localStorage.getItem('babblegram_game_mode') as 'daily' | 'practice' | null;
        const currentMode = urlMode || storedMode || 'daily';
        
        // Set the mode in localStorage if it's not already set or different
        if (!storedMode || storedMode !== currentMode) {
          localStorage.setItem('babblegram_game_mode', currentMode);
        }
        
        const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
        const fromWelcomeModeSpecific = localStorage.getItem(`${prefix}from_welcome_page`) === 'true';
        
        if (fromWelcomeLS || fromWelcomeSS || fromWelcomeModeSpecific) {
          console.log('GameRouteGuard: Coming from welcome page, skipping redirect check', {
            fromWelcomeLS,
            fromWelcomeSS,
            fromWelcomeModeSpecific,
            currentMode
          });
          return;
        }
        
        // Check if this is the first load in this session OR if cookies were cleared
        // If cookies are cleared but sessionStorage remains, we should treat it as first load
        const sessionLoaded = sessionStorage.getItem('babblegram_page_loaded') === 'true';
        
        // Check for ANY game data in either mode to detect if cookies were cleared
        const hasAnyCookieData = directCookieAccess.getCookie('babblegram_daily_current_level') || 
                                directCookieAccess.getCookie('babblegram_practice_current_level') ||
                                directCookieAccess.getCookie('babblegram_daily_saved_guesses') ||
                                directCookieAccess.getCookie('babblegram_practice_saved_guesses') ||
                                directCookieAccess.getCookie('babblegram_daily_current_word') ||
                                directCookieAccess.getCookie('babblegram_practice_current_word');
        
        const isFirstLoad = !sessionLoaded || !hasAnyCookieData;
        
        // Check if there's an explicit redirect cookie
        const redirectToWelcome = directCookieAccess.getCookie('redirect_to_welcome') === 'true';
        
        // Check for shared sequence and handle atomically
        if (sharedSequenceStateManager.shouldProcessSharedSequence()) {
          console.log('Shared sequence needs processing, handling atomically');
          
          try {
            const wasProcessed = await sharedSequenceStateManager.processSharedSequence();
            
            if (wasProcessed) {
              // Force page reload to ensure clean initialization in practice mode
              console.log('Shared sequence processed, reloading for clean initialization');
              window.location.reload();
              return;
            }
          } catch (error) {
            console.error('Error processing shared sequence:', error);
          }
        }
        
        // If shared sequence is active, skip all redirect logic
        if (sharedSequenceStateManager.isSharedSequenceActive()) {
          console.log('Shared sequence is active, skipping redirect checks');
          return;
        }
        
        // Check for game state in the CURRENT mode specifically
        const currentModeInProgress = directCookieAccess.hasInProgressGame(currentMode);
        const currentModeCompleted = directCookieAccess.hasCompletedGame(currentMode);
        const currentModeHasAnyGame = directCookieAccess.hasAnyGame(currentMode);
        
        // Also check other mode for logging
        const otherMode = currentMode === 'daily' ? 'practice' : 'daily';
        const otherModeInProgress = directCookieAccess.hasInProgressGame(otherMode);
        const otherModeCompleted = directCookieAccess.hasCompletedGame(otherMode);
        
        // Check if current mode has a level 1 game with no actual progress
        const currentLevel = directCookieAccess.getCookie(`${prefix}current_level`);
        const savedGuesses = directCookieAccess.getCookie(`${prefix}saved_guesses`);
        const hasActualGuesses = savedGuesses && savedGuesses !== '[]' && savedGuesses !== 'null';
        const isLevel1WithNoProgress = currentLevel === '1' && !hasActualGuesses;
        
        // Check if shared sequence is currently active for logging
        const isSharedSequenceActive = sharedSequenceStateManager.isSharedSequenceActive();
        
        console.log('GameRouteGuard: Mode-aware redirect check', {
          isFirstLoad,
          redirectToWelcome,
          currentMode,
          isSharedSequenceActive,
          currentModeInProgress,
          currentModeCompleted,
          currentModeHasAnyGame,
          otherModeInProgress,
          otherModeCompleted,
          currentLevel,
          hasActualGuesses,
          isLevel1WithNoProgress,
          sessionLoaded,
          hasAnyCookieData,
          pathname: location.pathname,
          timestamp: new Date().toISOString()
        });
        
        // CRITICAL FIX: Redirect logic based on current mode state:
        // 1. Explicit redirect cookie (always redirect)
        // 2. First load and accessing /game directly without any game state (but NOT if shared sequence)
        // 3. First load and level 1 with no actual progress in current mode (but NOT if shared sequence)
        const shouldRedirect = redirectToWelcome || 
                             (isFirstLoad && !currentModeHasAnyGame && !isSharedSequenceActive) ||
                             (isFirstLoad && isLevel1WithNoProgress && !isSharedSequenceActive);
        
        if (shouldRedirect) {
          const reason = redirectToWelcome 
            ? 'explicit redirect cookie' 
            : !currentModeHasAnyGame
              ? `no game state in ${currentMode} mode on direct access`
              : `level 1 with no progress in ${currentMode} mode on direct access`;
              
          console.log('GameRouteGuard: Redirecting to welcome page', {
            reason,
            currentMode,
            currentModeInProgress,
            currentModeCompleted,
            currentModeHasAnyGame,
            isLevel1WithNoProgress,
            timestamp: new Date().toISOString()
          });
          
          // Clear the redirect cookie to prevent loops
          if (redirectToWelcome) {
            directCookieAccess.deleteCookie('redirect_to_welcome');
          }
          
          navigate('/');
        } else {
          console.log('GameRouteGuard: Allowing access to game', {
            reason: currentModeCompleted ? `completed game in ${currentMode} mode` : `in-progress game in ${currentMode} mode`,
            currentMode,
            currentModeInProgress,
            currentModeCompleted,
            timestamp: new Date().toISOString()
          });
        }
        
        // Mark that page has been loaded in this session
        sessionStorage.setItem('babblegram_page_loaded', 'true');
      };
      
      // Add a small delay to allow state systems to initialize
      setTimeout(checkRedirect, 50);
    }
  }, [location.pathname, navigate, checked, switchToMode]);

  return <>{children}</>;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  
  // Check for clear parameter on location changes to work from any page
  const CheckClearParameter = () => {
    const location = useLocation();
    
    useEffect(() => {
      checkAndClearGameData();
    }, [location]);
    
    return null;
  };
  
  // Add some basic initialization logic and error handling
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Simple initialization check to catch any early errors
        console.log("App initializing...");
        
        // CRITICAL: Run comprehensive legacy cookie migration first
        const { LegacyCookieMigration } = await import('./lib/managers/LegacyCookieMigration');
        LegacyCookieMigration.migrate();
        
        // CRITICAL: Initialize StreakManager early to trigger streak-specific migration
        const { StreakManager } = await import('./components/game/managers/StreakManager');
        StreakManager.getInstance();
        
        // CRITICAL: Check for clear parameter (after migration)
        checkAndClearGameData();
        
        // CRITICAL: Check for shared sequences immediately on app load
        if (sharedSequenceStateManager.shouldProcessSharedSequence()) {
          console.log('Shared sequence detected on app initialization, processing atomically');
          
          try {
            const wasProcessed = await sharedSequenceStateManager.processSharedSequence();
            
            if (wasProcessed) {
              // Force page reload to ensure clean initialization in practice mode
              console.log('Shared sequence processed, reloading for clean initialization');
              window.location.reload();
              return;
            }
          } catch (error) {
            console.error('Error processing shared sequence on app init:', error);
          }
        }
        
        // Simulate checking critical app dependencies
        setTimeout(() => {
          setIsLoading(false);
          console.log("App initialized successfully");
        }, 100);
      } catch (error) {
        console.error("Error during app initialization:", error);
        setIsLoading(false);
      }
    };
    
    initializeApp();
    
    // Reset animation coordinator on app init
    animationCoordinator.reset();
    
    // Clean up on unmount
    return () => {
      animationCoordinator.reset();
      timeoutManager.clearAllTimeouts();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GameModeProvider>
        <ViewModeProvider>
          <GameConfigProvider>
          <DebugDateProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner position="top-center" closeButton theme="dark" richColors />
                <SharedSequenceNotification />
                <BrowserRouter>
                <ScrollToTop />
                <CheckClearParameter />
                <Routes>
                  {/* Entry point is the welcome page */}
                  <Route path="/" element={<WelcomePage />} />
                  
                  <Route element={<Layout />}>
                    {/* Game mode selector */}
                    <Route path="/modes" element={<Index />} />
                    
                    {/* Game route with guard to check for redirects */}
                    <Route path="/game" element={
                      <GameRouteGuard>
                        <GamePage />
                      </GameRouteGuard>
                    } />
                    <Route path="/babbleranks" element={<BabbleRanks />} />
                    <Route path="/how-to-play" element={<HowToPlay />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                  </Route>

                  {/* Admin */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminGate><AdminPosts /></AdminGate>} />
                  <Route path="/admin/posts/:id" element={<AdminGate><AdminPostEditor /></AdminGate>} />
                  <Route path="/admin/stats" element={<AdminGate><AdminStats /></AdminGate>} />
                  
                  {/* Redirect any other routes back to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </DebugDateProvider>
          </GameConfigProvider>
        </ViewModeProvider>
      </GameModeProvider>
    </QueryClientProvider>
  );
};

export default App;
